import React, { Component } from 'react';
import CodeMarkdown from './CodeMarkdown';
import ReactMarkdown from 'react-markdown';
import _ from 'lodash';
import ContentBox from './ContentBox';
import AceEditor from './AceEditor';
import AudioRecorder from './AudioRecorder';
import {callIfNotVisible, rndId} from './util';
import cn from "classnames";

const getCodeEditorPropsFor = (name, {fullscreenCodeWindow, setFullscreenCodeWindow, renderFullscreenToolbarElements}) => {
    return {
        isFullscreen: fullscreenCodeWindow === name,
        setFullscreen: (isFullscreen) => setFullscreenCodeWindow(isFullscreen ? name : null),
        renderFullscreenToolbarElements: renderFullscreenToolbarElements
    };
};

const TextAnswer = ({question, questionState, setQuestionState}) => {
    let rowCount = questionState.value ? questionState.value.split('\n').length : 0;
    let value = questionState.value ?? question.Inhalt ?? "";

    rowCount =  Math.max(5, rowCount);

    return (
        <div>
            <div className='block'><i>Your answer:</i></div>
            <textarea
                className="form-control textarea-answer"
                value={value}
                rows={rowCount}
                onChange={(e) => setQuestionState({ value: e.target.value })} />
        </div>
    );
};

class VoiceAnswer extends React.Component {
    onChange = (blob) => {
        const id = rndId();
        this.props.cacheRecording(blob, id);
        this.props.setQuestionState({ value: id });
    };

    render() {
        return (
            <div>
                <AudioRecorder onChange={this.onChange} shouldAutoSubmit={this.props.shouldAutoSubmit} />
            </div>
        );
    }
};

const CodeAnswer = (language) => ({question, questionState, setQuestionState, codeEditorProps}) => {

    return (
        <div>
            <div className='block'><i>Your answer:</i></div>
            <AceEditor
                mode={language}
                value={questionState.value ?? question.Inhalt}
                onChange={(value) => setQuestionState({ value })}
                {...getCodeEditorPropsFor('answer', codeEditorProps)}
                alwaysAllowFullscren
            />
        </div>
    );
};

const ChoiceAnswer = ({question, questionState, setQuestionState, codeEditorProps}) => {
    const renderChoice = (choice, i) => {
        let key = `choice_${choice.id}`,
            name = `question_${question.id}`,
            onChange = (e) =>  setQuestionState({ value: e.target.value });

        return (
            <div key={key}>
                <div className="form-check" key={key}>
                    <input
                        className="form-check-input"
                        type="radio"
                        name={name}
                        id={key}
                        value={choice.id}
                        checked={choice.id === questionState.value}
                        onChange={onChange}
                    />
                    <label className="form-check-label" htmlFor={key}>
                        {choice.Name}
                    </label>
                </div>
                {choice.description && choice.description.length &&
                <div className='block'>
                    <CodeMarkdown source={choice.description} codeEditorProps={{...codeEditorProps, codeEditorId: choice.id}} />
                </div>}
            </div>
        );
    };

    return (
        <div className="choice-list">
            {question.possibleAnswers.map(renderChoice)}
        </div>
    );
};

const MultipleChoiceAnswer = ({question, questionState, setQuestionState, codeEditorProps }) => {
    const renderChoice = (choice, i) => {
        let key = `choice_${choice.id}`,
            name = `question_${question.id}`,
            onChange = (e) =>  {
                let value = e.target.checked,
                    stateValue = questionState.value ? questionState.value : [];

                if(value && !stateValue.indexOf(choice.id) > -1) {
                    stateValue = [...stateValue, choice.id];
                    return setQuestionState({value: stateValue});
                }

                if(!value) {
                    stateValue = stateValue.filter(id => id !== choice.id);
                    return setQuestionState({value: stateValue});
                }
            };

        let isChecked = questionState.value && questionState.value.indexOf(choice.id) > -1;

        return (
            <div>
                <div className="form-check" key={key}>
                    <input
                        className="form-check-input"
                        type="checkbox"
                        name={name}
                        id={key}
                        checked={isChecked}
                        onChange={onChange}
                    />
                    <label className="form-check-label" htmlFor={key}>
                        {choice.Name}
                    </label>
                </div>
                {choice.description && choice.description.length &&
                <div className='block'>
                    <CodeMarkdown source={choice.description} codeEditorProps={{...codeEditorProps, codeEditorId: choice.id}} />
                </div>}
            </div>
        );
    };

    return (
        <div>
            <div className="block"><i>One or many answers can be correct.</i></div>
            <div className="choice-list">
                {question.possibleAnswers.map(renderChoice)}
            </div>
        </div>
    );
};

const questionTypeMapping = {
    Text: TextAnswer,
    Choice: ChoiceAnswer,
    'Multiple Choice': MultipleChoiceAnswer,
    javascript: CodeAnswer('javascript'),
    php: CodeAnswer('php'),
    jsx: CodeAnswer('jsx'),
    sql: CodeAnswer('sql'),
    css: CodeAnswer('css'),
    html: CodeAnswer('html'),
    voice: VoiceAnswer,
};

export default class QuizPage extends Component {
	static propTypes = {
	
	};

	state = {
		secondsLeft: 0,
        isSubmitting: false,
	};

	ticker = null;

	componentDidMount = () => {
	    this.setTimer();
	};

	componentDidUpdate = (prevProps) => {
        if(this.props.startTime !== prevProps.startTime) {
            this.setTimer();
        }

	    if(this.props.question.id !== prevProps.question.id) {
            this.setTimer();
        }
	};

    setTimer = () => {
        clearInterval(this.ticker);
        if (this.props.question.Duration) {
            this.ticker = setInterval(this.tick, 1000);
            this.tick();
        }
    }

    componentWillUnmount = () => {
    	clearInterval(this.ticker);
	};

    getSecondsLeft = (duration = null) => {
        duration = duration !== null ? duration : this.props.question.Duration;

        let secondsPassed = (Date.now()-this.props.startTime)/1000,
            allowedSeconds = duration * 60,
            secondsLeft = allowedSeconds - secondsPassed;


        return secondsLeft;
    };

    getRunOutOfTime = (duration) => {
        return this.getSecondsLeft(duration) < 0
    };

    getRunOutOfTimeEarly = () => {
        let earlyDuration = this.props.question.Duration ? this.props.question.Duration-3 : null;
        return this.getRunOutOfTime(earlyDuration);
    }

	tick = () => {
        if(this.getRunOutOfTime()) {
            clearInterval(this.ticker);
            callIfNotVisible(() => alert("The timer ran out, all your changes would be recorded. Let's move to the next question."));
        	this.props.submitQuestion();
		}

		this.setState({ secondsLeft: this.getSecondsLeft() });
	};

    getTimerString() {
        let secondsLeft = this.state.secondsLeft,
            minutesLeft = Math.floor(secondsLeft / 60),
            hoursLeft = Math.floor(minutesLeft / 60),
            minutesRest = Math.floor(minutesLeft % 60),
            secondsRest  = Math.round(secondsLeft % 60),
            remainingStr = '';

        if (secondsRest === 60) minutesRest++;
        remainingStr += _.padStart(hoursLeft, 2, "0");
        remainingStr += ':';

        remainingStr += _.padStart(minutesRest, 2, "0");
        remainingStr += ':';

        secondsRest = secondsRest === 60 ? 0 : secondsRest;
        remainingStr += _.padStart(secondsRest, 2, "0");;

        return remainingStr;
    }

	submitTitle = () => {
        let duration = this.props.question.Duration,
			secondsLeft = this.state.secondsLeft;

		if(!duration || secondsLeft < 1) {
			return 'Submit';
		}

		return 'Submit';
	};


    renderTimer = () => {
        let duration = this.props.question.Duration,
			secondsLeft = this.state.secondsLeft

        if (!duration) {
            return null;
        } 
        
        if (secondsLeft < 0) {
            return "00:00"
        }

        return this.getTimerString()
    }

	renderSubmitButton = () => {
        let {
            questionState,
            submitQuestion,
        } = this.props;

        let isDisabled = this.state.isSubmitting || !questionState.value || !questionState.value.length;

        const onSubmit = async () => {
            if(isDisabled) { return; }

            this.setState({ isSubmitting: true });

            await submitQuestion();

            this.setState({ isSubmitting: false });
        };

	    return (
            <button
                disabled={isDisabled}
                type="submit"
                className="btn btn-primary question-submit"
                onClick={onSubmit}
            >{this.submitTitle()}</button>
        );
    };

	setFullscreenCodeWindow = (fullscreenCodeWindow) => {
	    return this.props.setQuestionState({ fullscreenCodeWindow });
    };

	renderFullscreenToolbarElements = () => {
	    return [
            <li>{this.renderSubmitButton()}</li>
        ];
    };

    getTimerClassName = () => {
        return cn("floating-timer", {
            "floating-timer-running-out": this.props.question.Duration && (
                !this.state.secondsLeft || this.getRunOutOfTimeEarly()
            ),
        })
    }

	render() {
		let {
			question,
			questionState,
			setQuestionState,
            cacheRecording,
		} = this.props;

        const QuestionComponent = questionTypeMapping[question.Type];
        const codeEditorProps = {
            fullscreenCodeWindow: questionState.fullscreenCodeWindow,
            setFullscreenCodeWindow: this.setFullscreenCodeWindow,
            renderFullscreenToolbarElements: this.renderFullscreenToolbarElements
        };

        return (
            <ContentBox className="question-box">
                <div className="block question-box-description">
                    <div className="question-overlay" />
                    <h1>{question.question}</h1>
                    <div style={{textAlign: 'justify'}}>
                        <CodeMarkdown
                            source={question.Beschreibung}
                            codeEditorProps={{...codeEditorProps, codeEditorId: 'question-text'}}
                            question={question}
                        />
                    </div>
                </div>
                <div>
                    <div className="inner-question-box">
                        <div className="block">
                            <QuestionComponent
                                question={question}
                                questionState={questionState}
                                setQuestionState={setQuestionState}
                                codeEditorProps={codeEditorProps}
                                cacheRecording={cacheRecording}
                                shouldAutoSubmit={this.getRunOutOfTimeEarly()}
                            />
                        </div>

                        <div className="block">
                            {this.renderSubmitButton()}
                        </div>
                    </div>
                </div>
                <div className={this.getTimerClassName()}>{this.renderTimer()}</div>
            </ContentBox>
        )
	}
}